import React, { useState, useEffect } from "react";
import { listSnapshots as getSnapshots, createSnapshot, restoreSnapshot, deleteSnapshot, getSnapshot } from "../api/snapshots.js";

export default function SnapshotPanel({ onClose, onRestore }) {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveName, setSaveName] = useState("");
  const [saveDesc, setSaveDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [comparing, setComparing] = useState(null); // snapshot id being compared

  const refresh = () => {
    getSnapshots().then(setSnapshots).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const handleSave = async () => {
    if (!saveName.trim()) return;
    setSaving(true);
    try {
      await createSnapshot(saveName.trim(), saveDesc.trim());
      setSaveName("");
      setSaveDesc("");
      refresh();
    } catch (err) {
      alert("Failed to save snapshot: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (id, name) => {
    if (!confirm(`Restore snapshot "${name}"? This will replace all current test cases.`)) return;
    try {
      await restoreSnapshot(id);
      onRestore();
    } catch (err) {
      alert("Failed to restore: " + err.message);
    }
  };

  const handleDelete = async (id, name) => {
    if (!confirm(`Delete snapshot "${name}"?`)) return;
    try {
      await deleteSnapshot(id);
      refresh();
    } catch (err) {
      alert("Failed to delete: " + err.message);
    }
  };

  const handleCompare = async (id) => {
    if (comparing === id) { setComparing(null); return; }
    setComparing(id);
  };

  return (
    <div style={s.overlay}>
      <div style={s.panel}>
        {/* Header */}
        <div style={s.panelHeader}>
          <h2 style={s.panelTitle}>Test Suite Versions</h2>
          <button style={s.closeBtn} onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </button>
        </div>

        {/* Save new snapshot */}
        <div style={s.saveSection}>
          <div style={s.saveLabel}>Save Current Test Suite</div>
          <input
            style={s.input}
            value={saveName}
            onChange={(e) => setSaveName(e.target.value)}
            placeholder="Version name (e.g., v1.0-baseline)"
            onKeyDown={(e) => e.key === "Enter" && handleSave()}
          />
          <input
            style={s.input}
            value={saveDesc}
            onChange={(e) => setSaveDesc(e.target.value)}
            placeholder="Description (optional)"
          />
          <button style={s.saveBtn} onClick={handleSave} disabled={saving || !saveName.trim()}>
            {saving ? "Saving..." : "Save Snapshot"}
          </button>
        </div>

        {/* Snapshot list */}
        <div style={s.listSection}>
          <div style={s.listLabel}>Saved Versions</div>
          {loading ? (
            <div style={s.empty}>Loading...</div>
          ) : snapshots.length === 0 ? (
            <div style={s.empty}>No snapshots yet. Save your first version above.</div>
          ) : (
            <div style={s.list}>
              {snapshots.map((snap) => (
                <div key={snap.id} style={s.snapCard}>
                  <div style={s.snapInfo}>
                    <div style={s.snapName}>{snap.name}</div>
                    <div style={s.snapMeta}>
                      {new Date(snap.createdAt).toLocaleString()} &middot; {snap.testCaseCount} test cases
                      {snap.description && <span> &middot; {snap.description}</span>}
                    </div>
                  </div>
                  <div style={s.snapActions}>
                    <button style={s.actionBtn} onClick={() => handleRestore(snap.id, snap.name)} title="Restore">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                        <path d="M3 3v5h5"/>
                      </svg>
                    </button>
                    <button style={s.actionBtnDanger} onClick={() => handleDelete(snap.id, snap.name)} title="Delete">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  overlay: {
    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(0,0,0,0.3)", display: "flex",
    justifyContent: "center", alignItems: "flex-start",
    paddingTop: 80, zIndex: 100,
  },
  panel: {
    background: "#fff", borderRadius: 12, width: 520,
    maxHeight: "70vh", overflow: "auto",
    boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
  },
  panelHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: "16px 20px", borderBottom: "1px solid #e5e5e5",
  },
  panelTitle: { fontSize: 16, fontWeight: 600, letterSpacing: "-0.02em" },
  closeBtn: {
    width: 30, height: 30, border: "none", borderRadius: 6,
    background: "transparent", color: "#999", display: "flex",
    alignItems: "center", justifyContent: "center",
  },
  saveSection: {
    padding: "16px 20px", borderBottom: "1px solid #e5e5e5",
    display: "flex", flexDirection: "column", gap: 8,
  },
  saveLabel: { fontSize: 13, fontWeight: 500, color: "#333" },
  input: {
    width: "100%", padding: "8px 12px", border: "1px solid #e5e5e5", borderRadius: 8,
    fontSize: 14, outline: "none", color: "#0f0f0f",
  },
  saveBtn: {
    padding: "7px 16px", background: "#0f0f0f", color: "#fff", border: "none",
    borderRadius: 8, fontSize: 13, fontWeight: 500, alignSelf: "flex-start",
  },
  listSection: { padding: "16px 20px" },
  listLabel: { fontSize: 13, fontWeight: 500, color: "#333", marginBottom: 10 },
  empty: { fontSize: 13, color: "#999", padding: "12px 0" },
  list: { display: "flex", flexDirection: "column", gap: 6 },
  snapCard: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "10px 12px", border: "1px solid #e5e5e5", borderRadius: 8,
  },
  snapInfo: { flex: 1 },
  snapName: { fontSize: 14, fontWeight: 500, color: "#0f0f0f" },
  snapMeta: { fontSize: 12, color: "#999", marginTop: 2 },
  snapActions: { display: "flex", gap: 2 },
  actionBtn: {
    width: 30, height: 30, border: "none", borderRadius: 6,
    background: "transparent", color: "#666", display: "flex",
    alignItems: "center", justifyContent: "center",
  },
  actionBtnDanger: {
    width: 30, height: 30, border: "none", borderRadius: 6,
    background: "transparent", color: "#999", display: "flex",
    alignItems: "center", justifyContent: "center",
  },
};
